/**
 * Storage Service
 * Specialized service for handling large data storage via IndexedDB
 * Following Single Responsibility Principle
 */
class StorageService {
  private dbName = "qmin-marketplace-db";
  private storeName = "marketplace-config";
  private version = 1;
  private isServer = typeof window === 'undefined';
  
  constructor() {
    if (typeof window !== 'undefined') {
      console.log("%c[StorageService] Initialized with database:", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;", this.dbName);
      console.log("%c[StorageService] Store name:", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;", this.storeName);
    }
  }
  
  /**
   * Opens a connection to IndexedDB
   * @returns Promise resolving to the database connection
   */
  private openDB(): Promise<IDBDatabase | null> {
    // Skip for server-side rendering
    if (this.isServer) {
      console.log("[StorageService] Server-side detected, skipping IndexedDB operations");
      return Promise.resolve(null);
    }
    
    console.log("%c[StorageService] Opening IndexedDB connection", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;");
    
    return new Promise((resolve, reject) => {
      try {
        if (!window.indexedDB) {
          console.error("[StorageService] IndexedDB not supported in this browser");
          return resolve(null);
        }
        
        const request = indexedDB.open(this.dbName, this.version);
        
        request.onerror = (event) => {
          console.error("[StorageService] Error opening database:", request.error);
          // Don't reject here, just return null - this allows the app to continue without crashing
          resolve(null);
        };
        
        request.onsuccess = (event) => {
          console.log("%c[StorageService] Database opened successfully", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;");
          
          const db = request.result;
          
          // Listen for database errors
          db.onerror = (event) => {
            console.error("[StorageService] Database error:", event);
          };
          
          resolve(db);
        };
        
        // Handle database upgrade/creation
        request.onupgradeneeded = (event) => {
          console.log("%c[StorageService] Database upgrade needed - creating object store", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;");
          const db = (event.target as IDBOpenDBRequest).result;
          
          // Only create object store if it doesn't exist
          if (!db.objectStoreNames.contains(this.storeName)) {
            const objectStore = db.createObjectStore(this.storeName, { keyPath: "id" });
            console.log("%c[StorageService] Object store created:", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;", this.storeName);
            
            // Add an index to find items by timestamp
            objectStore.createIndex("timestamp", "timestamp", { unique: false });
            console.log("%c[StorageService] Added timestamp index", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;");
          }
        };
      } catch (error) {
        console.error("[StorageService] Critical error opening IndexedDB:", error);
        // Don't reject here, just return null
        resolve(null);
      }
    });
  }
  
  /**
   * Stores data in IndexedDB
   * @param key The key to store the data under
   * @param value The data to store
   */
  async setItem(key: string, value: any): Promise<void> {
    // Skip for server-side rendering
    if (this.isServer) {
      return Promise.resolve();
    }
    
    console.log("%c[StorageService] Setting item with key:", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;", key);
    
    try {
      const db = await this.openDB();
      // If db is null, we're in an environment without IndexedDB
      if (!db) {
        console.log("[StorageService] No database available, skipping");
        return Promise.resolve();
      }
      
      return new Promise((resolve, reject) => {
        try {
          const transaction = db.transaction(this.storeName, "readwrite");
          const store = transaction.objectStore(this.storeName);
          
          const data = { 
            id: key, 
            data: value, 
            timestamp: Date.now() 
          };
          
          console.log("%c[StorageService] Storing data:", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;", data);
          
          const request = store.put(data);
          
          request.onerror = () => {
            console.error("[StorageService] Error storing data:", request.error);
            reject(request.error);
          };
          
          request.onsuccess = () => {
            console.log("%c[StorageService] Data stored successfully with key:", "background: #9C27B0; color: white; padding: 3px; border-radius: 2px;", key);
            resolve();
          };
          
          // Close the database when the transaction is complete
          transaction.oncomplete = () => {
            console.log("[StorageService] Transaction complete, closing database");
            db.close();
          };
        } catch (error) {
          console.error("[StorageService] Transaction error:", error);
          reject(error);
        }
      });
    } catch (error) {
      console.error("[StorageService] Failed to set item:", error);
      return Promise.resolve();
    }
  }
  
  /**
   * Retrieves data from IndexedDB
   * @param key The key to retrieve
   * @returns The stored data or null if not found
   */
  async getItem<T>(key: string): Promise<{ data: T; timestamp: number } | null> {
    // Skip for server-side rendering
    if (this.isServer) {
      return Promise.resolve(null);
    }
    
    const db = await this.openDB();
    // If db is null, we're in an environment without IndexedDB
    if (!db) {
      return Promise.resolve(null);
    }
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        if (request.result) {
          resolve({
            data: request.result.data,
            timestamp: request.result.timestamp
          });
        } else {
          resolve(null);
        }
      };
      
      // Close the database when the transaction is complete
      transaction.oncomplete = () => db.close();
    });
  }
  
  /**
   * Removes data from IndexedDB
   * @param key The key to remove
   */
  async removeItem(key: string): Promise<void> {
    // Skip for server-side rendering
    if (this.isServer) {
      return Promise.resolve();
    }
    
    const db = await this.openDB();
    // If db is null, we're in an environment without IndexedDB
    if (!db) {
      return Promise.resolve();
    }
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      
      // Close the database when the transaction is complete
      transaction.oncomplete = () => db.close();
    });
  }
  
  /**
   * Checks if data for a key exists and is still valid (not expired)
   * @param key The key to check
   * @param maxAge Maximum age in milliseconds
   * @returns Whether the data exists and is not expired
   */
  async isValid(key: string, maxAge: number): Promise<boolean> {
    // Skip for server-side rendering
    if (this.isServer) {
      return Promise.resolve(false);
    }
    
    const result = await this.getItem(key);
    if (!result) return false;
    
    const now = Date.now();
    return (now - result.timestamp) < maxAge;
  }
}

// Singleton pattern for storage service
export const storageService = new StorageService();
